import { useState } from 'react';
import {
  DeleteButton,
  ErrorBanner,
  Field,
  InlineEdit,
  Loading,
  Modal,
  PageHead,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { EditableCriterion, EditableDimension, QuestionSet } from '../../lib/types';

/**
 * Editing the OML question set.
 *
 * The template has always been data rather than code, but until now changing a
 * question meant editing `db/seed/012_oml_v3_eos_question_set.sql` and running
 * a deploy. This is that, in the app, for admins.
 *
 * TWO THINGS THE SCREEN HAS TO SAY OUT LOUD, because both are invisible and
 * both bite:
 *
 *   - REWORDING A QUESTION RE-LABELS ITS HISTORY. The scores stay attached to
 *     the criterion, so every past assessment now reads as though it was asked
 *     the new question. Unavoidable — it is the same row — so the count of
 *     answers already recorded is on every question, and in the dialog before
 *     anybody types.
 *   - A SECTION'S DEPARTMENT IS NOT ITS NAME. The department says which part
 *     of the business the section is scoring; it does not retitle it, and it
 *     is many-to-one on purpose — five sections sit inside Engineering here.
 *     Changing it changes which department the business review reads its
 *     maturity from, which is why the control says so rather than sitting
 *     there unlabelled.
 *   - AND THE AREA IS A SEPARATE QUESTION from the department, for the same
 *     reason: Engineering covers four business areas, so the section has to
 *     say for itself which slice of the process library its improvement plan
 *     proposes from. See migration 183.
 *
 * Order and structure are the API's rules, not this page's — see the block
 * comment on the routes for why weights, and adding or removing a whole
 * section, are deliberately not here.
 */
export function QuestionEditor({ onBack }: { onBack: () => void }) {
  const set = useResource<QuestionSet>('/assessments/templates/OML/questions');
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditableCriterion | null>(null);
  const [adding, setAdding] = useState<EditableDimension | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = (err: unknown, fallback: string) =>
    setError(err instanceof Error ? err.message : fallback);

  /** Swaps a question with its neighbour, writing both positions. */
  const move = async (list: EditableCriterion[], index: number, direction: -1 | 1) => {
    const a = list[index];
    const b = list[index + direction];
    if (!a || !b) return;
    setBusy(true);
    setError(null);
    try {
      /* Both rows, because a swap that moves only one leaves the two sharing a
         position and the order stops being stable. */
      await Promise.all([
        api.put(`/assessments/criteria/${a.CriterionId}`, { sortOrder: b.SortOrder }),
        api.put(`/assessments/criteria/${b.CriterionId}`, { sortOrder: a.SortOrder }),
      ]);
      set.reload();
    } catch (err) {
      fail(err, 'Could not reorder the questions.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (criterion: EditableCriterion) => {
    setError(null);
    try {
      await api.del(`/assessments/criteria/${criterion.CriterionId}`);
      set.reload();
    } catch (err) {
      /* The API refuses to delete an answered question and says why. That
         sentence is the useful one, so it goes straight through. */
      fail(err, 'Could not delete that question.');
    }
  };

  /** Writes one field of a section, leaving the others alone — see the API. */
  const saveDimension = (id: number, patch: Record<string, unknown>, whenItFails: string) =>
    api
      .put(`/assessments/dimensions/${id}`, patch)
      .then(() => set.reload())
      .catch((err: unknown) => fail(err, whenItFails));

  if (set.loading) return <Loading label="Loading the question set" />;
  if (set.error) return <ErrorBanner message={set.error} />;
  if (!set.data) return null;

  /* Held in a local so the lists below stay narrowed inside the section
     callbacks, which `set.data` does not — it is a mutable property. */
  const data = set.data;
  const total = data.dimensions.reduce((n, d) => n + d.criteria.length, 0);

  return (
    <>
      <PageHead
        title="OML questions"
        lede={`${data.Name} — version ${data.Version}. ${data.dimensions.length} sections, ${total} questions, scored ${data.ScaleMin}–${data.ScaleMax}.`}
        actions={
          <button type="button" className="btn btn-ghost" onClick={onBack}>
            ← Back to assessments
          </button>
        }
      />

      {error && <ErrorBanner message={error} />}

      <section className="card">
        <div className="card-body">
          <p className="small">
            <span className="strong">Changing a question changes its history.</span> Scores stay
            attached to the question they were recorded against, so rewording one makes every past
            assessment read as though it had asked the new wording. The count beside each question
            is how many answers that would re-label.
          </p>
          <p className="xs muted mt-2">
            Edits made here survive deploys — the question set is re-seeded on every release, and a
            question you have edited is skipped. A question nobody has answered can be deleted; one
            with answers behind it cannot, and should be reworded instead.
          </p>
        </div>
      </section>

      {data.dimensions.map((dimension) => (
        <section className="card mt-4" key={dimension.DimensionId}>
          <div className="card-head row-between wrap">
            <div className="grow">
              <span className="eyebrow">Section</span>
              <h2>{dimension.Name}</h2>
            </div>
            <span className="badge badge-neutral">{dimension.criteria.length} questions</span>
          </div>

          <div className="card-body">
            <Field label="Section note" hint="Optional — shown under the heading while scoring.">
              <InlineEdit
                value={dimension.Description}
                placeholder="Nothing shown under this heading."
                onSave={(next) => {
                  void saveDimension(
                    dimension.DimensionId,
                    { description: next.trim() || null },
                    'Could not save the section note.',
                  );
                }}
              />
            </Field>

            <div className="form-grid">
              <Field
                label="Department it belongs to"
                hint="Which department this section is about. Several sections can share one, and it changes neither the heading nor any score."
              >
                <select
                  className="select"
                  value={dimension.DepartmentId ?? ''}
                  onChange={(e) => {
                    void saveDimension(
                      dimension.DimensionId,
                      { departmentId: e.target.value === '' ? null : Number(e.target.value) },
                      'Could not save the department.',
                    );
                  }}
                >
                  <option value="">Not linked to a department</option>
                  {data.departments.map((d) => (
                    <option key={d.DepartmentId} value={d.DepartmentId}>
                      {d.Name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Business area scored"
                hint="Two things read this. The improvement plan proposes this area's processes, and the business review gives this section's score to the unit on the same area — so changing it moves a number in the quarterly review. Its own area, not the department's: Engineering covers four of them."
              >
                <select
                  className="select"
                  value={dimension.AreaId ?? ''}
                  onChange={(e) => {
                    void saveDimension(
                      dimension.DimensionId,
                      { areaId: e.target.value === '' ? null : Number(e.target.value) },
                      'Could not save the business area.',
                    );
                  }}
                >
                  <option value="">No area — propose nothing, score nothing</option>
                  {data.areas.map((a) => (
                    <option key={a.AreaId} value={a.AreaId}>
                      {a.Name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="eos-list mt-4">
              {dimension.criteria.map((criterion, index) => (
                <QuestionRow
                  key={criterion.CriterionId}
                  criterion={criterion}
                  index={index}
                  count={dimension.criteria.length}
                  busy={busy}
                  onMove={(direction) => void move(dimension.criteria, index, direction)}
                  onEdit={() => setEditing(criterion)}
                  onDelete={() => void remove(criterion)}
                />
              ))}
            </div>

            <button
              type="button"
              className="btn btn-secondary btn-sm mt-4"
              onClick={() => setAdding(dimension)}
            >
              Add a question to {dimension.Name}
            </button>
          </div>
        </section>
      ))}

      {editing && (
        <QuestionDialog
          criterion={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            set.reload();
          }}
        />
      )}

      {adding && (
        <QuestionDialog
          dimension={adding}
          onClose={() => setAdding(null)}
          onSaved={() => {
            setAdding(null);
            set.reload();
          }}
        />
      )}
    </>
  );
}

/** The five rungs a question offers, or null when it was never given any. */
function parseLevels(json: string | null): Record<string, string> | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as Record<string, string>;
  } catch {
    /* Same rule the scoring page follows: a malformed descriptor blob falls
       back to the bare buttons rather than taking the screen down. */
    return null;
  }
}

const LEVELS = ['1', '2', '3', '4', '5'] as const;

function QuestionRow({
  criterion,
  index,
  count,
  busy,
  onMove,
  onEdit,
  onDelete,
}: {
  criterion: EditableCriterion;
  index: number;
  count: number;
  busy: boolean;
  onMove: (direction: -1 | 1) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const levels = parseLevels(criterion.LevelDescriptors);

  return (
    <div className="eos-row">
      <span className="eos-prio">{index + 1}</span>
      <div className="eos-move">
        <button
          type="button"
          aria-label={`Move "${criterion.Text}" up`}
          disabled={busy || index === 0}
          onClick={() => onMove(-1)}
        >
          ▲
        </button>
        <button
          type="button"
          aria-label={`Move "${criterion.Text}" down`}
          disabled={busy || index === count - 1}
          onClick={() => onMove(1)}
        >
          ▼
        </button>
      </div>

      <div className="grow">
        <div className="title">{criterion.Text}</div>
        {levels ? (
          <div className="xs muted">
            {LEVELS.map((n) => levels[n] ?? '—').join(' · ')}
          </div>
        ) : (
          <div className="xs subtle">No written answers — scored on the bare 1–5 buttons.</div>
        )}
        <div className="xs subtle">
          {criterion.AnswerCount === 0
            ? 'Never answered'
            : `${criterion.AnswerCount} answer${criterion.AnswerCount === 1 ? '' : 's'} across ${criterion.AssessmentCount} assessment${criterion.AssessmentCount === 1 ? '' : 's'}`}
          {criterion.EditedUtc && ` · edited ${formatDate(criterion.EditedUtc)}`}
        </div>
      </div>

      {criterion.AnswerCount > 0 && (
        <span className="eos-pill neutral">{criterion.AnswerCount}</span>
      )}
      <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
        Edit
      </button>
      {/* Answered questions keep their delete button, and the API refuses it
          with a sentence saying why. Hiding it would leave somebody hunting for
          a control that is missing for a reason nobody stated. */}
      <DeleteButton
        label="✕"
        what={`the question "${criterion.Text}"`}
        onConfirm={onDelete}
      />
    </div>
  );
}

/**
 * Writing a question and its five answers.
 *
 * One dialog for both new and existing, because the fields are identical and
 * the only difference is which endpoint it posts to. The rungs are five plain
 * boxes rather than a repeater: there are always exactly five, they are the
 * scale, and their order is the whole meaning — least mature at 1.
 */
function QuestionDialog({
  criterion,
  dimension,
  onClose,
  onSaved,
}: {
  criterion?: EditableCriterion;
  dimension?: EditableDimension;
  onClose: () => void;
  onSaved: () => void;
}) {
  const existing = parseLevels(criterion?.LevelDescriptors ?? null);
  const [text, setText] = useState(criterion?.Text ?? '');
  const [levels, setLevels] = useState<Record<string, string>>(() =>
    Object.fromEntries(LEVELS.map((n) => [n, existing?.[n] ?? ''])),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const answered = criterion?.AnswerCount ?? 0;

  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    setError(null);
    try {
      /* All five or none. A half-filled ladder renders as gaps beside the
         numbers while scoring, which reads as a bug rather than as a question
         that was never given written answers. */
      const filled = LEVELS.filter((n) => levels[n]?.trim());
      const body = {
        text: text.trim(),
        levelDescriptors:
          filled.length === LEVELS.length
            ? Object.fromEntries(LEVELS.map((n) => [n, levels[n]!.trim()]))
            : null,
      };
      if (criterion) {
        await api.put(`/assessments/criteria/${criterion.CriterionId}`, body);
      } else if (dimension) {
        await api.post(`/assessments/dimensions/${dimension.DimensionId}/criteria`, body);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that question.');
      setSaving(false);
    }
  };

  const partial =
    LEVELS.some((n) => levels[n]?.trim()) && !LEVELS.every((n) => levels[n]?.trim());

  return (
    <Modal
      title={criterion ? 'Edit question' : `New question — ${dimension?.Name ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !text.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      {answered > 0 && (
        <p className="small">
          <span className="strong">
            {answered} answer{answered === 1 ? '' : 's'} already recorded
          </span>{' '}
          across {criterion?.AssessmentCount} assessment
          {criterion?.AssessmentCount === 1 ? '' : 's'}. Those scores stay where they are, so
          changing the wording changes what they will read as.
        </p>
      )}

      <div className={answered > 0 ? 'mt-4' : ''}>
        <Field label="Question" hint="What is being asked, not what a good answer looks like.">
          <textarea
            className="textarea"
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </Field>
      </div>

      <div className="divider mt-4" />
      <div className="eos-section-title">
        The five answers, least mature first
      </div>
      <p className="xs subtle">
        The answer somebody picks is the score. Leave all five blank for a question scored on the
        bare 1–5 buttons instead.
      </p>

      <div className="stack mt-2" style={{ gap: 'var(--space-2)' }}>
        {LEVELS.map((n) => (
          <Field key={n} label={`Level ${n}`}>
            <input
              className="input"
              value={levels[n] ?? ''}
              onChange={(e) => setLevels((prev) => ({ ...prev, [n]: e.target.value }))}
            />
          </Field>
        ))}
      </div>

      {partial && (
        <p className="xs subtle mt-2">
          Some levels are blank, so this will save with no written answers at all and be scored on
          the bare buttons. Fill all five to keep the ladder.
        </p>
      )}
    </Modal>
  );
}
